import type {
  ServerAuthDescriptor,
  ServerAuthPolicy,
  ServerSessionState,
  ServerWebSocketTicketResult,
} from "@t3tools/contracts";
import { EnvironmentHttpCommonError } from "@t3tools/contracts";
import type { EnvironmentHttpCommonError as EnvironmentHttpCommonErrorType } from "@t3tools/contracts";
import * as Data from "effect/Data";
import * as Effect from "effect/Effect";
import * as Predicate from "effect/Predicate";
import * as Schema from "effect/Schema";
import { HttpClientError } from "effect/unstable/http";
import * as Socket from "effect/unstable/socket/Socket";
import { create } from "zustand";

import { browserAccountStorage, reconcileAccount } from "../../accountStorage";
import { platformApiBaseUrl } from "../../shared/auth/platform-api-origin";
import { onWebAddress } from "../../shared/auth/web-address";
import { attachServerToken, serverTokenSubject } from "./serverToken";
import { ensureInferenceCredential } from "./inferenceCredential";
import { PrimaryEnvironmentHttpClient } from "./httpClient";
import {
  probeWorkspace,
  reloadOnce,
  type WorkspaceRefusal,
  type WorkspaceUnavailableState,
} from "./workspaceState";
import { runPrimaryHttp } from "../../lib/runtime";
import { t } from "~/i18n";

export class BootstrapHttpError extends Data.TaggedError("BootstrapHttpError")<{
  readonly message: string;
  readonly status: number;
}> {}
const isBootstrapHttpError = (u: unknown): u is BootstrapHttpError =>
  Predicate.isTagged(u, "BootstrapHttpError");
const isEnvironmentHttpCommonError = Schema.is(EnvironmentHttpCommonError);

export type ServerAuthGateState =
  | { status: "authenticated" }
  | {
      status: "requires-auth";
      /** Absent on the web address when the router said signed out: no server answered. */
      auth?: ServerAuthDescriptor;
      errorMessage?: string;
    }
  /**
   * Signed in, and the server refuses the account's token for its platform:
   * the account has no access to the desktop app or to its team's web
   * workspace. Signing in again mints the same token, so the window says so in
   * place of the app.
   */
  | { status: "platform-not-allowed"; policy: ServerAuthPolicy }
  /**
   * The web address's router refused a signed-in person's session: no access
   * to the web version, no team, or a workspace that is not connected,
   * stopped, starting, or out. The window shows the state in place of the app.
   */
  | {
      status: "workspace-unavailable";
      refusal: WorkspaceRefusal & { readonly state: WorkspaceUnavailableState };
    };

/** Whether the gate reached a server, whose descriptor the window then reads. */
export function gateReachedServer(state: ServerAuthGateState): boolean {
  return (
    state.status !== "workspace-unavailable" &&
    !(state.status === "requires-auth" && state.auth === undefined)
  );
}

/**
 * The connected server's auth policy, once the bootstrap has read it. A surface
 * that exists on one kind of server only (a team server's team management)
 * reads it here; `null` until the server has answered.
 */
export const useServerAuthPolicy = create<{ readonly policy: ServerAuthPolicy | null }>(() => ({
  policy: null,
}));

let bootstrapPromise: Promise<ServerAuthGateState> | null = null;
let resolvedAuthenticatedGateState: ServerAuthGateState | null = null;

/**
 * `GET /api/auth/token` on platform-api — session-cookie gated, so `fetch`
 * carries `credentials: "include"` and bypasses the server's own HTTP client,
 * which targets a different origin entirely. A 401 means the
 * browser holds no Dovod session; that is a normal state, not a failure.
 */
export async function acquireServerToken(): Promise<string | null> {
  return retryTransientBootstrap(fetchServerToken);
}

async function fetchServerToken(): Promise<string | null> {
  const response = await fetch(`${platformApiBaseUrl}/api/auth/token`, {
    credentials: "include",
  });

  if (response.status === 401) {
    return null;
  }

  if (!response.ok) {
    throw new BootstrapHttpError({
      message: t("environments/failedToAcquireThe_q3lmrp"),
      status: response.status,
    });
  }

  const body = (await response.json()) as { readonly token: string };
  return body.token;
}

export async function fetchSessionState(): Promise<ServerSessionState> {
  return retryTransientBootstrap(async () => {
    try {
      return await runPrimaryHttp(
        PrimaryEnvironmentHttpClient.pipe(
          Effect.flatMap((client) => client.auth.session({ headers: {} })),
        ),
      );
    } catch (error) {
      const status = readHttpApiStatus(error);
      const statusLabel = String(status ?? "unknown");
      throw new BootstrapHttpError({
        message: t("environments/failedToLoadServer_tygrpg", { statusLabel }),
        status: status ?? 500,
      });
    }
  });
}

/**
 * `POST /api/auth/websocket-ticket` on the server. The bearer header is
 * attached centrally (`lib/runtime.ts`'s `primaryHttpRuntime`), so every call
 * here just passes the bearer token currently held in memory.
 */
export async function issueWebSocketTicket(): Promise<ServerWebSocketTicketResult> {
  return retryTransientBootstrap(async () => {
    try {
      return await runPrimaryHttp(
        PrimaryEnvironmentHttpClient.pipe(
          Effect.flatMap((client) => client.auth.webSocketTicket({ headers: {} })),
        ),
      );
    } catch (error) {
      const status = readHttpApiStatus(error);
      if (
        (HttpClientError.isHttpClientError(error) &&
          (error.reason._tag === "TransportError" ||
            (error.reason._tag === "DecodeError" &&
              status !== null &&
              status >= 200 &&
              status < 300 &&
              isTransientBootstrapError(error.reason.cause)))) ||
        (status !== null && TRANSIENT_BOOTSTRAP_STATUS_CODES[status] === true)
      ) {
        throw new Socket.SocketError({
          reason: new Socket.SocketOpenError({ kind: "Unknown", cause: error }),
        });
      }
      throw new BootstrapHttpError({
        message: t("environments/failedToLoadServer_tygrpg", {
          statusLabel: String(status ?? "unknown"),
        }),
        status: status ?? 500,
      });
    }
  });
}

function readHttpApiStatus(error: unknown): number | null {
  if (isEnvironmentHttpCommonError(error)) {
    return readEnvironmentHttpErrorStatus(error);
  }
  return HttpClientError.isHttpClientError(error) && error.response !== undefined
    ? error.response.status
    : null;
}

function readEnvironmentHttpErrorStatus(error: EnvironmentHttpCommonErrorType): number {
  switch (error._tag) {
    case "EnvironmentRequestInvalidError":
      return 400;
    case "EnvironmentAuthInvalidError":
      return 401;
    case "EnvironmentScopeRequiredError":
      return 403;
    case "EnvironmentInternalError":
      return 500;
  }
}

const TRANSIENT_BOOTSTRAP_STATUS_CODES: Record<number, true> = { 502: true, 503: true, 504: true };
const BOOTSTRAP_RETRY_TIMEOUT_MS = 15_000;
const BOOTSTRAP_RETRY_STEP_MS = 500;

export async function retryTransientBootstrap<T>(operation: () => Promise<T>): Promise<T> {
  const startedAt = Date.now();
  while (true) {
    try {
      return await operation();
    } catch (error) {
      if (!isTransientBootstrapError(error)) {
        throw error;
      }

      if (Date.now() - startedAt >= BOOTSTRAP_RETRY_TIMEOUT_MS) {
        throw error;
      }

      await waitForBootstrapRetry(BOOTSTRAP_RETRY_STEP_MS);
    }
  }
}

function waitForBootstrapRetry(delayMs: number): Promise<void> {
  const { promise, resolve } = Promise.withResolvers<void>();
  setTimeout(resolve, delayMs);
  return promise;
}

function isTransientBootstrapError(error: unknown): boolean {
  if (isBootstrapHttpError(error)) {
    return TRANSIENT_BOOTSTRAP_STATUS_CODES[error.status] === true;
  }

  if (error instanceof TypeError) {
    return true;
  }

  return error instanceof DOMException && error.name === "AbortError";
}

/** Keeps the router pending while the page unloads. */
function reloading(): Promise<never> {
  return new Promise<never>(() => {});
}

/**
 * Makes the browser's stored account data the token's account's
 * (`reconcileAccount`), on the web address. Resolves to whether the page
 * reloads to drop another account's data it hydrated.
 */
async function adoptTokenAccount(token: string): Promise<boolean> {
  const subject = serverTokenSubject(token);
  if (subject === null) return false;
  let outcome: "kept" | "reload";
  try {
    const reconciliation = reconcileAccount(browserAccountStorage(), subject);
    outcome = reconciliation.outcome;
    void reconciliation.cleanup.catch((error: unknown) => {
      console.warn("[dovod] could not erase the browser's account databases", error);
    });
  } catch (error) {
    // A storage the browser refuses holds nothing of anyone's to erase.
    console.warn("[dovod] could not reconcile the browser's account data", error);
    return false;
  }
  if (outcome === "kept") return false;
  window.location.reload();
  return true;
}

/**
 * The gate for a session the web router refused. A signed-in person's stored
 * data is reconciled with their account all the same, from one token request
 * without the bootstrap's retries: a page that says the workspace is down must
 * not first wait out a down gateway.
 */
async function gateForRefusal(refusal: WorkspaceRefusal): Promise<ServerAuthGateState> {
  switch (refusal.state) {
    case "signed_out":
      return { status: "requires-auth" };
    case "identity_mismatch":
      // A token minted from the current session matches it, and a fresh load
      // mints one.
      return reloadOnce()
        ? reloading()
        : { status: "workspace-unavailable", refusal: { state: "outage", retryAfterMs: null } };
    case "no_team":
    case "no_web_access":
    case "not_connected":
    case "stopped":
    case "starting":
    case "outage": {
      const token = await fetchServerToken().catch(() => null);
      if (token !== null && (await adoptTokenAccount(token))) return reloading();
      return {
        status: "workspace-unavailable",
        refusal: { state: refusal.state, retryAfterMs: refusal.retryAfterMs },
      };
    }
  }
}

/**
 * Mints a token from the Dovod session cookie (if any), hands it to the
 * server, and reports whether the server accepted it. `auth`, when present, is
 * always read fresh from `/api/auth/session` — including `legacyStateNotice`,
 * which the sign-in surface renders when a first run over
 * device-paired local state left a retirement notice behind.
 *
 * On the web address the router answers first, for the session's workspace: a
 * session it routes nowhere gets the state the router names.
 */
async function bootstrapServerAuth(): Promise<ServerAuthGateState> {
  const web = onWebAddress();
  if (web) {
    const probe = await probeWorkspace();
    if (probe.kind === "refused") return gateForRefusal(probe.refusal);
  }

  let token: string | null = null;
  let errorMessage: string | undefined;
  try {
    token = await acquireServerToken();
  } catch (error) {
    errorMessage = error instanceof Error ? error.message : String(error);
  }

  if (web && token !== null && (await adoptTokenAccount(token))) return reloading();
  attachServerToken(token);

  const session = await fetchSessionState();
  useServerAuthPolicy.setState({ policy: session.auth.policy });
  if (token !== null && session.authenticated) {
    // Inference needs the user's `dvk_`, and only the window can mint one, on
    // the desktop and on a team server alike. Deliberately not awaited into the
    // gate's result: an entitlement gap or an unreachable platform-api must not
    // keep a signed-in user out of their workspace, which works offline by
    // design. A missing credential surfaces where it matters — the first turn
    // reports it.
    if (session.subject !== undefined) {
      void ensureInferenceCredential(session.subject).then((outcome) => {
        if (outcome.status !== "held") {
          console.warn("[dovod] inference credential unavailable", outcome);
        }
      });
    }
    return { status: "authenticated" };
  }

  // Either there was no token to offer, or the server refused the one we had
  // (revoked, wrong audience, wrong platform, clock skew past tolerance) —
  // either way the window holds nothing worth keeping.
  attachServerToken(null);
  if (session.rejection === "platform_not_allowed") {
    return { status: "platform-not-allowed", policy: session.auth.policy };
  }
  return {
    status: "requires-auth",
    auth: session.auth,
    ...(errorMessage ? { errorMessage } : {}),
  };
}

export async function resolveInitialServerAuthGateState(): Promise<ServerAuthGateState> {
  if (resolvedAuthenticatedGateState?.status === "authenticated") {
    return resolvedAuthenticatedGateState;
  }

  if (bootstrapPromise) {
    return bootstrapPromise;
  }

  const nextPromise = bootstrapServerAuth();
  bootstrapPromise = nextPromise;
  return nextPromise
    .then((result) => {
      if (result.status === "authenticated") {
        resolvedAuthenticatedGateState = result;
      }
      return result;
    })
    .finally(() => {
      if (bootstrapPromise === nextPromise) {
        bootstrapPromise = null;
      }
    });
}

/**
 * Drops the cached gate state and the in-memory token so the next
 * `resolveInitialServerAuthGateState()` re-bootstraps from scratch. Used both
 * by tests and by the live session-revocation watcher — a `revoked`
 * poll result must not leave a stale "authenticated" memo behind.
 */
export function invalidateServerAuthGateState(): void {
  bootstrapPromise = null;
  resolvedAuthenticatedGateState = null;
  attachServerToken(null);
}

export function __resetServerAuthBootstrapForTests() {
  invalidateServerAuthGateState();
}
